import type { Tutorial, RoleKey } from '../types'

export const tutorials: Tutorial[] = [
  // === GETTING STARTED (1-6) ===
  {
    id: 'tut-001',
    slug: 'bienvenido-smartdental',
    title: 'Bienvenido a SmartDental: tu primer dia',
    description: 'Conoce los conceptos basicos de la aplicacion y como esta organizada.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 5,
    content: {
      sections: [
        {
          heading: 'Bienvenido',
          body: 'SmartDental es un ERP dental integral disenado para optimizar la gestion de tu clinica. En este tutorial conoceras la estructura basica de la aplicacion.',
          callout: { type: 'info', text: 'Este tutorial es recomendado para todos los usuarios nuevos, independientemente de su rol.' },
        },
        {
          heading: 'Estructura de la aplicacion',
          body: 'La barra lateral izquierda te da acceso a todos los modulos de la aplicacion. Segun tu rol, veras diferentes opciones. El dashboard principal muestra un resumen de la actividad relevante para ti.',
        },
        {
          heading: 'Perfil y configuracion',
          body: 'Haz clic en tu nombre en la parte inferior del sidebar para acceder a tu perfil, configuracion o cerrar sesion. Desde ahi puedes personalizar tu experiencia.',
        },
      ],
    },
    tags: ['inicio', 'bienvenida', 'navegacion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-002',
    slug: 'navegar-aplicacion',
    title: 'Navegar por la aplicacion',
    description: 'Aprende a moverte entre modulos, usar el buscador y los atajos de teclado.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'interactive-tour',
    duration: 3,
    content: {
      sections: [
        {
          heading: 'Tour interactivo',
          body: 'Este tutorial incluye un tour guiado por la interfaz. Haz clic en "Iniciar tour" para comenzar.',
          callout: { type: 'tip', text: 'Puedes reiniciar el tour de onboarding en cualquier momento desde tu perfil.' },
        },
      ],
    },
    tags: ['navegacion', 'tour', 'interfaz'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-003',
    slug: 'busqueda-rapida-atajos',
    title: 'Busqueda rapida y atajos de teclado (Ctrl+K)',
    description: 'Domina el Command Palette para navegar a velocidad sin usar el raton.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Pulsa Ctrl+K (o Cmd+K en Mac) para abrir la paleta de comandos.' },
        { heading: 'Paso 2', body: 'Escribe el nombre de una pagina, paciente o accion. Los resultados aparecen al instante.' },
        { heading: 'Paso 3', body: 'Usa las flechas arriba/abajo para navegar y Enter para seleccionar.' },
        {
          body: 'Atajos utiles: N para nueva cita, Ctrl+K para buscar.',
          callout: { type: 'tip', text: 'En movil, pulsa el icono de lupa en la barra superior.' },
        },
      ],
    },
    tags: ['atajos', 'teclado', 'busqueda', 'ctrl+k'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-004',
    slug: 'configurar-perfil',
    title: 'Configurar tu perfil y preferencias',
    description: 'Personaliza tu cuenta, foto, notificaciones y preferencias de la app.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Haz clic en tu nombre en la parte inferior del sidebar.' },
        { heading: 'Paso 2', body: 'Selecciona "Mi perfil" para editar tus datos personales.' },
        { heading: 'Paso 3', body: 'Ve a "Configuracion" para ajustar notificaciones y preferencias.' },
      ],
    },
    tags: ['perfil', 'configuracion', 'preferencias'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-005',
    slug: 'notificaciones-gestion',
    title: 'Notificaciones: que significan y como gestionarlas',
    description: 'Entiende los distintos tipos de notificaciones y como configurarlas a tu gusto.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Tipos de notificaciones', body: 'Las notificaciones se dividen en: alertas clinicas (citas, pacientes), financieras (pagos, facturas), del sistema (actualizaciones) y de comunicacion (mensajes, recordatorios).' },
        { heading: 'Gestion', body: 'Haz clic en la campana de la barra superior para ver las notificaciones recientes. Las no leidas se marcan con un punto azul.' },
        { callout: { type: 'info', text: 'Puedes desactivar tipos especificos de notificaciones desde tu perfil.' } },
      ],
    },
    tags: ['notificaciones', 'alertas', 'configuracion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },
  {
    id: 'tut-006',
    slug: 'dashboard-rol',
    title: 'El dashboard: tu centro de control segun tu rol',
    description: 'Descubre que informacion se muestra en tu dashboard y como interpretarla.',
    category: 'getting-started',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 4,
    content: {
      sections: [
        { heading: 'Dashboard de Recepcionista', body: 'Muestra citas del dia, lista de espera, pacientes sin check-in y alertas de cobros pendientes.' },
        { heading: 'Dashboard de Dentista', body: 'Incluye citas del dia, tratamientos activos, consentimientos pendientes y laboratorio en curso.' },
        { heading: 'Dashboard de Manager', body: 'KPIs de ingresos, ocupacion, productividad por doctor y tendencias del mes.' },
      ],
    },
    tags: ['dashboard', 'inicio', 'kpi'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 6,
  },

  // === AGENDA (7-14) ===
  {
    id: 'tut-007',
    slug: 'como-agendar-cita',
    title: 'Como agendar una cita paso a paso',
    description: 'Aprende a reservar citas desde el calendario usando el BookingWizard.',
    category: 'agenda',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST', 'DENTIST'],
    format: 'interactive-tour',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre el calendario desde el sidebar o pulsa N.' },
        { heading: 'Paso 2', body: 'Haz clic en un hueco libre del calendario.' },
        { heading: 'Paso 3', body: 'Selecciona el paciente, profesional, tipo de cita y duracion.' },
        { heading: 'Paso 4', body: 'Confirma y guarda la cita.' },
        { callout: { type: 'tip', text: 'Puedes arrastrar y soltar citas para reagendarlas rapidamente.' } },
      ],
    },
    tags: ['cita', 'agenda', 'reserva', 'calendario'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-008',
    slug: 'vistas-calendario',
    title: 'Vistas del calendario: dia, semana, mes y lista',
    description: 'Cambia entre las distintas vistas del calendario segun tu necesidad.',
    category: 'agenda',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En la parte superior del calendario veras las pestanas: Dia, Semana, Mes, Lista.' },
        { heading: 'Paso 2', body: 'Haz clic en cada pestana para cambiar la vista.' },
        { heading: 'Paso 3', body: 'En vista Lista puedes filtrar por profesional, estado o tipo de cita.' },
        { callout: { type: 'info', text: 'La vista Dia es ideal para la gestion diaria de recepcion.' } },
      ],
    },
    tags: ['calendario', 'vistas', 'dia', 'semana', 'mes'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-009',
    slug: 'confirmar-cancelar-reagendar',
    title: 'Confirmar, cancelar y reagendar citas',
    description: 'Gestiona los cambios de estado de las citas de forma eficiente.',
    category: 'agenda',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Confirmar una cita', body: 'Haz clic en la cita y selecciona "Confirmar". El paciente recibira una notificacion si los recordatorios estan activos.' },
        { heading: 'Cancelar una cita', body: 'Haz clic en la cita, selecciona "Cancelar" y elige un motivo. Puedes ofrecer reagendar en el mismo flujo.' },
        { heading: 'Reagendar', body: 'Arrastra la cita a un nuevo hueco o usa el boton "Reagendar" para abrir el wizard.' },
        { callout: { type: 'warning', text: 'Las citas canceladas quedan registradas en el historial del paciente.' } },
      ],
    },
    tags: ['cita', 'confirmar', 'cancelar', 'reagendar'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-010',
    slug: 'lista-espera',
    title: 'Gestionar la lista de espera',
    description: 'Aprende a manejar pacientes en espera y asignarles huecos disponibles.',
    category: 'agenda',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Accede a "Lista de espera" desde el sidebar.' },
        { heading: 'Paso 2', body: 'Anade un paciente indicando preferencias de horario, profesional y urgencia.' },
        { heading: 'Paso 3', body: 'Cuando haya una cancelacion, el sistema sugerira pacientes de la lista de espera.' },
        { heading: 'Paso 4', body: 'Haz clic en "Asignar hueco" para mover al paciente al calendario.' },
      ],
    },
    tags: ['lista de espera', 'espera', 'hueco', 'cancelacion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-011',
    slug: 'bloques-personales',
    title: 'Bloques personales: almuerzo, formacion, vacaciones',
    description: 'Bloquea huecos en tu agenda para ausencias y actividades no clinicas.',
    category: 'agenda',
    difficulty: 'beginner',
    roles: ['DENTIST', 'HYGIENIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En el calendario, haz clic derecho en un hueco y selecciona "Bloquear".' },
        { heading: 'Paso 2', body: 'Elige el tipo de bloque: almuerzo, reunion, formacion o vacaciones.' },
        { heading: 'Paso 3', body: 'Ajusta la duracion y confirma. El bloque aparecera en gris en tu agenda.' },
        { callout: { type: 'tip', text: 'Los bloques de vacaciones se pueden configurar desde el perfil para periodos largos.' } },
      ],
    },
    tags: ['bloques', 'agenda', 'almuerzo', 'vacaciones'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },
  {
    id: 'tut-012',
    slug: 'recordatorios-automaticos',
    title: 'Enviar recordatorios automaticos a pacientes',
    description: 'Configura el envio de recordatorios por email y SMS.',
    category: 'agenda',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a "Recordatorios" en el sidebar.' },
        { heading: 'Paso 2', body: 'Configura las plantillas de recordatorio para cada canal (email y SMS).' },
        { heading: 'Paso 3', body: 'Define el horario de envio (por ejemplo, 24h antes de la cita).' },
        { heading: 'Paso 4', body: 'Activa los recordatorios y monitoriza el estado de envio.' },
      ],
    },
    tags: ['recordatorios', 'sms', 'email', 'cita'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 6,
  },
  {
    id: 'tut-013',
    slug: 'que-hacer-no-show',
    title: 'Que hacer ante un no-show',
    description: 'Protocolo de actuacion cuando un paciente no acude a su cita.',
    category: 'agenda',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Marcar el no-show', body: 'Despues de 15 min de espera, marca la cita como "No-show" desde el menu contextual.' },
        { heading: 'Contactar al paciente', body: 'El sistema sugiere enviar un mensaje al paciente para reagendar.' },
        { heading: 'Gestionar la lista de espera', body: 'Ofrece el hueco a un paciente de la lista de espera.' },
        { callout: { type: 'warning', text: 'Tres no-shows consecutivos activan una alerta en la ficha del paciente.' } },
      ],
    },
    tags: ['no-show', 'ausencia', 'cita', 'protocolo'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 7,
  },
  {
    id: 'tut-014',
    slug: 'flujo-post-cita',
    title: 'Flujo post-cita: facturar, agendar seguimiento',
    description: 'Acciones recomendadas tras finalizar una cita clinica.',
    category: 'agenda',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST', 'DENTIST'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Marca la cita como "Completada".' },
        { heading: 'Paso 2', body: 'Si hay tratamiento realizado, genera la factura desde el drawer de la cita.' },
        { heading: 'Paso 3', body: 'Programa la proxima revision o cita de seguimiento.' },
        { heading: 'Paso 4', body: 'Registra la evolucion clinica si aun no esta hecha.' },
        { callout: { type: 'tip', text: 'Puedes automatizar parte de este flujo desde Automatizaciones.' } },
      ],
    },
    tags: ['post-cita', 'facturacion', 'seguimiento', 'flujo'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 8,
  },

  // === PATIENTS (15-23) ===
  {
    id: 'tut-015',
    slug: 'crear-paciente',
    title: 'Crear un nuevo paciente con todos los datos',
    description: 'Guia completa para registrar un paciente nuevo en el sistema.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'interactive-tour',
    duration: 5,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde Pacientes, haz clic en "Nuevo paciente".' },
        { heading: 'Paso 2', body: 'Completa los datos personales: nombre, DNI, fecha de nacimiento, telefono y email.' },
        { heading: 'Paso 3', body: 'Anade la direccion y datos de contacto de emergencia.' },
        { heading: 'Paso 4', body: 'Registra alergias, medicacion y antecedentes medicos.' },
        { callout: { type: 'info', text: 'El email es obligatorio para enviar presupuestos y consentimientos digitales.' } },
      ],
    },
    tags: ['paciente', 'nuevo', 'registro', 'ficha'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-016',
    slug: 'buscar-pacientes',
    title: 'Buscar pacientes: filtros, alertas y estados',
    description: 'Tecnicas avanzadas para encontrar pacientes rapidamente.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Usa Ctrl+K para buscar por nombre, telefono o DNI.' },
        { heading: 'Paso 2', body: 'En la pagina de Pacientes, usa los filtros laterales: por profesional asignado, estado de tratamiento o fecha de ultima visita.' },
        { heading: 'Paso 3', body: 'Los pacientes con alertas medicas se marcan con un icono rojo.' },
        { callout: { type: 'tip', text: 'Guarda tus filtros habituales como vistas personalizadas.' } },
      ],
    },
    tags: ['paciente', 'busqueda', 'filtros', 'alertas'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-017',
    slug: 'ficha-paciente-tabs',
    title: 'La ficha del paciente: guia de las 11 tabs',
    description: 'Recorrido completo por todas las secciones de la ficha de un paciente.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['DENTIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 6,
    content: {
      sections: [
        { heading: '1. Datos personales', body: 'Informacion basica, contacto y foto del paciente.' },
        { heading: '2. Historial clinico', body: 'Todas las evoluciones registradas, ordenadas cronologicamente.' },
        { heading: '3. Odontograma', body: 'Representacion grafica del estado dental actual.' },
        { heading: '4. Tratamientos', body: 'Planes de tratamiento activos e historicos.' },
        { heading: '5. Presupuestos', body: 'Presupuestos enviados, aceptados o rechazados.' },
        { heading: '6. Documentos', body: 'Radiografias, fotos, informes y documentos adjuntos.' },
        { heading: '7. Facturas', body: 'Historial de facturacion del paciente.' },
        { heading: '8. Consentimientos', body: 'Consentimientos firmados y pendientes.' },
        { heading: '9. Seguros', body: 'Polizas asociadas y coberturas.' },
        { heading: '10. Comunicaciones', body: 'Emails y SMS enviados.' },
        { heading: '11. Timeline', body: 'Vista cronologica de toda la actividad del paciente.' },
      ],
    },
    tags: ['ficha', 'paciente', 'tabs', 'historial'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-018',
    slug: 'registrar-alergias',
    title: 'Registrar alergias y alertas medicas',
    description: 'Como documentar alertas clinicas para que aparezcan en el momento oportuno.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre la ficha del paciente y ve a la pestana "Datos personales".' },
        { heading: 'Paso 2', body: 'En la seccion "Alertas medicas", haz clic en "Anadir alerta".' },
        { heading: 'Paso 3', body: 'Selecciona el tipo (alergia, enfermedad, medicacion) y describe los detalles.' },
        { heading: 'Paso 4', body: 'Marca "Mostrar al iniciar tratamiento" para alertas criticas.' },
        { callout: { type: 'warning', text: 'Las alertas criticas se muestran en un modal al abrir la ficha del paciente.' } },
      ],
    },
    tags: ['alergias', 'alertas', 'medicas', 'paciente'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-019',
    slug: 'odontograma',
    title: 'El odontograma: como registrar el estado dental',
    description: 'Guia visual para usar el odontograma interactivo.',
    category: 'patients',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'interactive-tour',
    duration: 5,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, abre la pestana "Odontograma".' },
        { heading: 'Paso 2', body: 'Selecciona una herramienta del panel lateral (caries, obturacion, extraccion...).' },
        { heading: 'Paso 3', body: 'Haz clic en el diente para aplicar el estado. Puedes hacer clic en caras especificas.' },
        { heading: 'Paso 4', body: 'Guarda el odontograma. El historial se conserva automaticamente.' },
        { callout: { type: 'tip', text: 'Usa la vista comparativa para ver la evolucion entre fechas.' } },
      ],
    },
    tags: ['odontograma', 'dientes', 'estado dental', 'caries'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },
  {
    id: 'tut-020',
    slug: 'portal-paciente-link',
    title: 'Generar y enviar el link del portal del paciente',
    description: 'Como dar acceso a los pacientes a su portal personal.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, haz clic en "Portal del paciente".' },
        { heading: 'Paso 2', body: 'Genera un link de acceso seguro. El link tiene validez limitada.' },
        { heading: 'Paso 3', body: 'Envialo por email directamente desde el sistema.' },
        { callout: { type: 'info', text: 'El paciente puede ver sus citas, presupuestos y documentos desde el portal.' } },
      ],
    },
    tags: ['portal', 'paciente', 'link', 'acceso'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 6,
  },

  // === PATIENTS cont. (21-23) ===
  {
    id: 'tut-021',
    slug: 'subir-documentos-ficha',
    title: 'Subir documentos a la ficha del paciente',
    description: 'Como adjuntar radiografias, fotos y documentos a la ficha clinica.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST', 'DENTIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre la ficha del paciente y ve a la pestana "Documentos".' },
        { heading: 'Paso 2', body: 'Haz clic en "Subir archivo" y selecciona la imagen o documento.' },
        { heading: 'Paso 3', body: 'Anade una descripcion y categoria (radiografia, foto intraoral, informe, etc.).' },
        { callout: { type: 'tip', text: 'Puedes subir multiples archivos a la vez arrastrandolos.' } },
      ],
    },
    tags: ['documentos', 'ficha', 'radiografia', 'adjuntos'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 7,
  },
  {
    id: 'tut-022',
    slug: 'exportar-pacientes-csv',
    title: 'Exportar pacientes a CSV',
    description: 'Genera un archivo CSV con los datos de los pacientes para analisis externo.',
    category: 'patients',
    difficulty: 'beginner',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Pacientes y haz clic en "Exportar".' },
        { heading: 'Paso 2', body: 'Selecciona los campos que quieres incluir en la exportacion.' },
        { heading: 'Paso 3', body: 'Aplica filtros si solo quieres un subconjunto de pacientes.' },
        { callout: { type: 'warning', text: 'Los datos exportados contienen informacion sensible. Asegurate de cumplir el RGPD.' } },
      ],
    },
    tags: ['exportar', 'csv', 'pacientes', 'datos'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 8,
  },
  {
    id: 'tut-023',
    slug: 'copiloto-clinico-ia',
    title: 'El copiloto clinico: sugerencias automaticas de IA',
    description: 'Como aprovechar las sugerencias inteligentes durante la consulta.',
    category: 'patients',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Que es el copiloto', body: 'El copiloto clinico analiza el historial del paciente, el odontograma y las evoluciones para sugerir tratamientos, alertas o proximas revisiones.' },
        { heading: 'Como usarlo', body: 'Aparece automaticamente en el lateral de la ficha del paciente cuando hay sugerencias relevantes. Haz clic en cualquier sugerencia para aplicarla o descartarla.' },
        { callout: { type: 'info', text: 'El copiloto es una herramienta de apoyo. La decision clinica siempre corresponde al profesional.' } },
      ],
    },
    tags: ['ia', 'copiloto', 'sugerencias', 'inteligencia artificial'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 9,
  },

  // === CLINICAL (24-27) ===
  {
    id: 'tut-024',
    slug: 'registrar-evolucion-clinica',
    title: 'Registrar una evolucion clinica',
    description: 'Guia paso a paso para documentar una visita en el historial del paciente.',
    category: 'clinical',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'interactive-tour',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, ve a "Nueva evolucion".' },
        { heading: 'Paso 2', body: 'Selecciona el tipo: rutinaria, urgencia o progreso de tratamiento.' },
        { heading: 'Paso 3', body: 'Describe los hallazgos, procedimientos realizados y observaciones.' },
        { heading: 'Paso 4', body: 'Vincula la evolucion a un tratamiento activo si aplica.' },
      ],
    },
    tags: ['evolucion', 'historial', 'consulta', 'clinica'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-025',
    slug: 'tipos-evolucion',
    title: 'Tipos de evolucion: rutinaria, urgencia, progreso',
    description: 'Entiende las diferencias entre los tipos de evolucion clinica.',
    category: 'clinical',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'article',
    duration: 2,
    content: {
      sections: [
        { heading: 'Evolucion rutinaria', body: 'Documentacion de una visita de control o revision periodica. Incluye estado general, higiene y observaciones.' },
        { heading: 'Evolucion de urgencia', body: 'Registro de una visita por dolor, trauma o emergencia. Prioriza el diagnostico rapido y el tratamiento inmediato.' },
        { heading: 'Evolucion de progreso', body: 'Seguimiento de un tratamiento en curso. Documenta las fases completadas y las pendientes.' },
      ],
    },
    tags: ['evolucion', 'tipos', 'rutinaria', 'urgencia', 'progreso'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-026',
    slug: 'vincular-evoluciones-tratamientos',
    title: 'Vincular evoluciones a tratamientos',
    description: 'Como asociar cada evolucion al plan de tratamiento correspondiente.',
    category: 'clinical',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Al crear una evolucion, busca el campo "Tratamiento asociado".' },
        { heading: 'Paso 2', body: 'Selecciona el tratamiento activo del listado.' },
        { heading: 'Paso 3', body: 'Guarda la evolucion. Ahora aparecera en el historial del tratamiento.' },
        { callout: { type: 'tip', text: 'Las evoluciones vinculadas permiten hacer seguimiento del progreso por tratamiento.' } },
      ],
    },
    tags: ['evolucion', 'tratamiento', 'vincular', 'seguimiento'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-027',
    slug: 'buscar-historial-clinico',
    title: 'Buscar en el historial clinico de un paciente',
    description: 'Tecnicas para encontrar informacion en el historial de forma eficiente.',
    category: 'clinical',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En la ficha del paciente, ve a la pestana "Historial clinico".' },
        { heading: 'Paso 2', body: 'Usa el buscador para filtrar por palabra clave, fecha o tipo de evolucion.' },
        { heading: 'Paso 3', body: 'Usa los filtros rapidos: "Ultimo mes", "Tratamientos activos", "Urgencias".' },
      ],
    },
    tags: ['historial', 'busqueda', 'filtros', 'evolucion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },

  // === TREATMENTS (28-31) ===
  {
    id: 'tut-028',
    slug: 'crear-plan-tratamiento',
    title: 'Crear un plan de tratamiento',
    description: 'Como disenar un plan completo con fases, procedimientos y presupuesto estimado.',
    category: 'treatments',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, ve a "Nuevo plan de tratamiento".' },
        { heading: 'Paso 2', body: 'Anade las fases del tratamiento en orden cronologico.' },
        { heading: 'Paso 3', body: 'Para cada fase, selecciona los procedimientos del catalogo.' },
        { heading: 'Paso 4', body: 'Asigna piezas dentales y estima la duracion de cada fase.' },
        { callout: { type: 'tip', text: 'Puedes usar la sugerencia de plan por IA como punto de partida.' } },
      ],
    },
    tags: ['tratamiento', 'plan', 'fases', 'procedimientos'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-029',
    slug: 'ciclo-vida-tratamiento',
    title: 'Ciclo de vida del tratamiento: planificado a completado',
    description: 'Entiende los estados por los que pasa un tratamiento y que acciones son posibles en cada uno.',
    category: 'treatments',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Planificado', body: 'El tratamiento esta creado pero aun no ha comenzado. Se puede editar completamente.' },
        { heading: 'En curso', body: 'Se ha iniciado el tratamiento. Las fases se van completando una a una.' },
        { heading: 'Pausado', body: 'El tratamiento se ha interrumpido temporalmente. Puede reanudarse.' },
        { heading: 'Completado', body: 'Todas las fases estan finalizadas. El tratamiento pasa al historial.' },
        { heading: 'Abandonado', body: 'El paciente decidio no continuar. Se archiva con motivo de abandono.' },
      ],
    },
    tags: ['tratamiento', 'estados', 'ciclo', 'completado'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-030',
    slug: 'presupuesto-a-tratamiento',
    title: 'Convertir un presupuesto aceptado en tratamiento',
    description: 'Flujo para transformar un presupuesto aprobado en un plan de tratamiento activo.',
    category: 'treatments',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Busca el presupuesto en la ficha del paciente y verifica que este "Aceptado".' },
        { heading: 'Paso 2', body: 'Haz clic en "Convertir a tratamiento".' },
        { heading: 'Paso 3', body: 'Revisa las fases y procedimientos importados desde el presupuesto.' },
        { heading: 'Paso 4', body: 'Ajusta fechas estimadas y asigna el profesional responsable.' },
        { callout: { type: 'info', text: 'Una vez convertido, el presupuesto queda vinculado al tratamiento para trazabilidad.' } },
      ],
    },
    tags: ['presupuesto', 'tratamiento', 'aceptado', 'convertir'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-031',
    slug: 'sugerencia-plan-ia',
    title: 'Usar la sugerencia de plan por IA',
    description: 'Como aprovechar las recomendaciones automaticas de planes de tratamiento.',
    category: 'treatments',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, haz clic en "Sugerir plan" junto a Nuevo tratamiento.' },
        { heading: 'Paso 2', body: 'La IA analiza el odontograma y las evoluciones para proponer un plan.' },
        { heading: 'Paso 3', body: 'Revisa cada fase sugerida, anade, elimina o modifica procedimientos.' },
        { heading: 'Paso 4', body: 'Confirma el plan final. La IA aprende de tus ajustes para futuras sugerencias.' },
        { callout: { type: 'warning', text: 'La sugerencia de IA es un punto de partida. Siempre valida clinicamente el plan propuesto.' } },
      ],
    },
    tags: ['ia', 'sugerencia', 'plan', 'tratamiento', 'inteligencia artificial'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },

  // === BUDGETS (32-36) ===
  {
    id: 'tut-032',
    slug: 'crear-presupuesto',
    title: 'Crear un presupuesto para un paciente',
    description: 'Guia completa para generar un presupuesto detallado vinculado al plan de tratamiento.',
    category: 'budgets',
    difficulty: 'beginner',
    roles: ['DENTIST', 'RECEPTIONIST'],
    format: 'interactive-tour',
    duration: 5,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, ve a "Presupuestos" y haz clic en "Nuevo".' },
        { heading: 'Paso 2', body: 'Selecciona si partes de un plan de tratamiento o creas uno nuevo.' },
        { heading: 'Paso 3', body: 'Anade lineas de presupuesto con procedimientos, cantidades y precios.' },
        { heading: 'Paso 4', body: 'Revisa el total, incluye notas y guarda el presupuesto.' },
      ],
    },
    tags: ['presupuesto', 'crear', 'paciente', 'lineas'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-033',
    slug: 'lineas-descuentos-seguro',
    title: 'Anadir lineas, descuentos y seguro',
    description: 'Como gestionar descuentos, promociones y cobertura de seguros en un presupuesto.',
    category: 'budgets',
    difficulty: 'intermediate',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Lineas de presupuesto', body: 'Cada linea representa un procedimiento. Puedes anadir multiples lineas, reorganizarlas y agruparlas por fase.' },
        { heading: 'Descuentos', body: 'Aplica descuentos por linea o descuento global. El sistema calcula automaticamente el total.' },
        { heading: 'Seguro', body: 'Si el paciente tiene poliza, selecciona la aseguradora y el sistema aplica la cobertura configurada.' },
        { callout: { type: 'tip', text: 'Los descuentos quedan auditados con quien los aplico y cuando.' } },
      ],
    },
    tags: ['presupuesto', 'descuentos', 'seguro', 'lineas'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-034',
    slug: 'enviar-presupuesto-paciente',
    title: 'Enviar el presupuesto al paciente',
    description: 'Como compartir el presupuesto por email para su aprobacion.',
    category: 'budgets',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre el presupuesto y haz clic en "Enviar al paciente".' },
        { heading: 'Paso 2', body: 'Selecciona el canal: email.' },
        { heading: 'Paso 3', body: 'Personaliza el mensaje si es necesario y envia.' },
        { callout: { type: 'info', text: 'El paciente recibe un link seguro donde puede ver, aceptar o rechazar el presupuesto.' } },
      ],
    },
    tags: ['presupuesto', 'enviar', 'email', 'paciente'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-035',
    slug: 'modo-presentacion-presupuesto',
    title: 'Modo presentacion: mostrar el presupuesto en consulta',
    description: 'Vista simplificada del presupuesto para explicar al paciente en la consulta.',
    category: 'budgets',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre el presupuesto y haz clic en "Modo presentacion".' },
        { heading: 'Paso 2', body: 'La vista muestra solo lo esencial: fases, procedimientos y totales, sin detalles tecnicos.' },
        { heading: 'Paso 3', body: 'Puedes navegar por fases y responder dudas del paciente.' },
        { callout: { type: 'tip', text: 'El modo presentacion funciona bien en tablets y pantallas de consulta.' } },
      ],
    },
    tags: ['presupuesto', 'presentacion', 'consulta', 'paciente'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-036',
    slug: 'seguimiento-presupuestos-enviados',
    title: 'Seguimiento de presupuestos enviados',
    description: 'Como monitorizar el estado de los presupuestos y su tasa de aceptacion.',
    category: 'budgets',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST', 'MANAGER'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Estados de un presupuesto', body: 'Borrador, Enviado, Visto, Aceptado, Rechazado, Caducado.' },
        { heading: 'Panel de seguimiento', body: 'En Presupuestos > Enviados puedes ver todos los presupuestos pendientes de respuesta.' },
        { heading: 'Tasa de aceptacion', body: 'El dashboard muestra el porcentaje de presupuestos aceptados vs rechazados, desglosado por profesional y tipo de tratamiento.' },
        { callout: { type: 'tip', text: 'Reenvia presupuestos caducados o haz seguimiento de los no vistos en 48h.' } },
      ],
    },
    tags: ['presupuesto', 'seguimiento', 'aceptacion', 'estado'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },

  // === BILLING (37-41) ===
  {
    id: 'tut-037',
    slug: 'crear-factura-desde-cita',
    title: 'Crear una factura desde una cita',
    description: 'Genera una factura directamente desde el calendario o la ficha de la cita.',
    category: 'billing',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'interactive-tour',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre la cita en el calendario y haz clic en "Facturar".' },
        { heading: 'Paso 2', body: 'El sistema prellena la factura con los tratamientos realizados en la cita.' },
        { heading: 'Paso 3', body: 'Revisa los importes, aplica descuentos si es necesario y guarda.' },
        { callout: { type: 'tip', text: 'Puedes facturar multiples citas del mismo paciente en una sola factura.' } },
      ],
    },
    tags: ['factura', 'cita', 'crear', 'calendario'],
    publishedAt: '2026-05-28',
    updatedAt: '2026-05-28',
    isNew: true,
    sortOrder: 1,
  },
  {
    id: 'tut-038',
    slug: 'registrar-pago',
    title: 'Registrar un pago (efectivo, tarjeta, transferencia)',
    description: 'Como registrar un cobro en el sistema con el metodo de pago correspondiente.',
    category: 'billing',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre la factura pendiente y haz clic en "Registrar pago".' },
        { heading: 'Paso 2', body: 'Selecciona el metodo de pago: efectivo, tarjeta de credito/debito, transferencia bancaria o Bizum.' },
        { heading: 'Paso 3', body: 'Introduce el importe recibido. Si es parcial, el sistema genera el saldo pendiente.' },
        { heading: 'Paso 4', body: 'Guarda el registro. El sistema emite un recibo y actualiza el estado de la factura.' },
        { callout: { type: 'info', text: 'Los pagos con tarjeta pueden sincronizarse automaticamente si tienes integracion con TPV.' } },
      ],
    },
    tags: ['pago', 'cobro', 'efectivo', 'tarjeta', 'transferencia'],
    publishedAt: '2026-05-28',
    updatedAt: '2026-05-28',
    isNew: true,
    sortOrder: 2,
  },
  {
    id: 'tut-039',
    slug: 'facturar-aseguradora-vs-privado',
    title: 'Facturar a aseguradora vs paciente privado',
    description: 'Diferencias y pasos para facturar a una compania de seguros o a un paciente particular.',
    category: 'billing',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'article',
    duration: 4,
    content: {
      sections: [
        { heading: 'Facturacion a paciente privado', body: 'El paciente paga el importe total al momento del servicio. La factura se emite a nombre del paciente con su NIF/CIF.' },
        { heading: 'Facturacion a aseguradora', body: 'La poliza del paciente determina el tipo de cobertura. El sistema calcula automaticamente la parte cubierta por el seguro y la parte a cargo del paciente.' },
        { heading: 'Documentacion requerida', body: 'Para aseguradoras: autorizacion previa, informe medico justificativo y copia de la poliza. El sistema genera estos documentos automaticamente desde la ficha del paciente.' },
        { heading: 'Reclamaciones', body: 'Si la aseguradora rechaza el pago, el sistema marca la factura como "rechazada" y notifica para gestionar el cobro directo al paciente.' },
        { callout: { type: 'warning', text: 'Algunas aseguradoras tienen plazos de reclamacion de 30 dias. Registra la factura cuanto antes.' } },
      ],
    },
    tags: ['factura', 'aseguradora', 'seguro', 'privado', 'cobertura'],
    publishedAt: '2026-05-28',
    updatedAt: '2026-05-28',
    isNew: true,
    sortOrder: 3,
  },
  {
    id: 'tut-040',
    slug: 'gestionar-facturas-vencidas',
    title: 'Gestionar facturas vencidas y cobros pendientes',
    description: 'Como identificar, reclamar y gestionar los impagos de forma eficiente.',
    category: 'billing',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST', 'MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Facturacion > Pendientes para ver todas las facturas vencidas ordenadas por dias de retraso.' },
        { heading: 'Paso 2', body: 'Selecciona una factura y haz clic en "Recordatorio". El sistema envia un email o SMS automatico al paciente.' },
        { heading: 'Paso 3', body: 'Si el pago no llega en 7 dias, utiliza la funcion "Llamada de cobro" para registrar el seguimiento telefonico.' },
        { heading: 'Paso 4', body: 'Para facturas superiores a 90 dias, considera el envio de burofax o la gestion con agencia de cobros.' },
        { callout: { type: 'tip', text: 'El panel de cobros muestra el importe total pendiente y el envejecimiento de la cartera para cada mes.' } },
      ],
    },
    tags: ['factura', 'vencida', 'cobro', 'pendiente', 'impago'],
    publishedAt: '2026-05-28',
    updatedAt: '2026-05-28',
    isNew: true,
    sortOrder: 4,
  },
  {
    id: 'tut-041',
    slug: 'anular-factura',
    title: 'Anular una factura correctamente',
    description: 'Procedimiento para anular una factura emitida manteniendo la trazabilidad fiscal.',
    category: 'billing',
    difficulty: 'intermediate',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Busca la factura en Facturacion > Emitidas y abre su detalle.' },
        { heading: 'Paso 2', body: 'Haz clic en "Anular factura". El sistema pedira un motivo de anulacion (obligatorio para auditoria).' },
        { heading: 'Paso 3', body: 'El sistema genera una factura rectificativa (abono) con la referencia a la factura original.' },
        { heading: 'Paso 4', body: 'Ambas facturas (original y rectificativa) quedan vinculadas y visibles en el historial del paciente.' },
        { callout: { type: 'warning', text: 'Una factura anulada no se puede deshacer. Si fue un error menor, considera emitir una nueva factura con la correccion.' } },
      ],
    },
    tags: ['factura', 'anular', 'rectificativa', 'abono', 'fiscal'],
    publishedAt: '2026-05-28',
    updatedAt: '2026-05-28',
    isNew: true,
    sortOrder: 5,
  },

  // === CONSENTS (42-46) ===
  {
    id: 'tut-042',
    slug: 'crear-plantilla-consentimiento',
    title: 'Crear una plantilla de consentimiento',
    description: 'Como disenar una plantilla reutilizable para distintos tratamientos.',
    category: 'consents',
    difficulty: 'beginner',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Consentimientos > Plantillas y haz clic en "Nueva plantilla".' },
        { heading: 'Paso 2', body: 'Escribe el texto del consentimiento usando el editor enriquecido.' },
        { heading: 'Paso 3', body: 'Anade variables como {{patientName}} para personalizacion automatica.' },
        { heading: 'Paso 4', body: 'Configura si es obligatorio y para que tratamientos aplica.' },
      ],
    },
    tags: ['consentimiento', 'plantilla', 'crear', 'variables'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-043',
    slug: 'variables-plantillas',
    title: 'Usar variables en las plantillas',
    description: 'Lista de variables disponibles y como funcionan.',
    category: 'consents',
    difficulty: 'intermediate',
    roles: ['ADMIN'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Variables de paciente', body: '{{patientName}}, {{patientDni}}, {{patientBirthDate}}, {{patientAddress}}' },
        { heading: 'Variables de clinica', body: '{{clinicName}}, {{clinicAddress}}, {{clinicPhone}}, {{doctorName}}' },
        { heading: 'Variables de tratamiento', body: '{{treatmentName}}, {{treatmentDate}}, {{procedureList}}' },
        { callout: { type: 'tip', text: 'Las variables se rellenan automaticamente al generar el consentimiento para un paciente especifico.' } },
      ],
    },
    tags: ['variables', 'plantilla', 'consentimiento', 'personalizacion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-044',
    slug: 'enviar-consentimiento-firma',
    title: 'Enviar un consentimiento al paciente para firma',
    description: 'Flujo para enviar un consentimiento digital y recibir la firma.',
    category: 'consents',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST', 'DENTIST'],
    format: 'interactive-tour',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, ve a la pestana "Consentimientos".' },
        { heading: 'Paso 2', body: 'Selecciona el consentimiento y haz clic en "Enviar para firma".' },
        { heading: 'Paso 3', body: 'El paciente recibe un link seguro por email.' },
        { heading: 'Paso 4', body: 'Una vez firmado, el sistema notifica y archiva el consentimiento firmado.' },
      ],
    },
    tags: ['consentimiento', 'firma', 'digital', 'enviar'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-045',
    slug: 'firmar-consentimiento-tablet',
    title: 'Firmar un consentimiento presencialmente (tablet)',
    description: 'Como usar el modo firma en tablet para que el paciente firme en la clinica.',
    category: 'consents',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Abre el consentimiento en el sistema.' },
        { heading: 'Paso 2', body: 'Haz clic en "Modo firma presencial".' },
        { heading: 'Paso 3', body: 'Entrega la tablet al paciente para que lea y firme con el dedo o stylus.' },
        { callout: { type: 'tip', text: 'El modo tablet oculta controles de navegacion para una experiencia mas limpia.' } },
      ],
    },
    tags: ['consentimiento', 'firma', 'tablet', 'presencial'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-046',
    slug: 'verificar-consentimientos-tratamiento',
    title: 'Verificar consentimientos antes de iniciar tratamiento',
    description: 'Como comprobar que todos los consentimientos necesarios estan firmados.',
    category: 'consents',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'article',
    duration: 2,
    content: {
      sections: [
        { heading: 'Verificacion automatica', body: 'Si un consentimiento obligatorio esta pendiente, el sistema muestra una alerta al abrir la ficha del paciente.' },
        { heading: 'Verificacion manual', body: 'En la pestana Consentimientos de la ficha puedes ver el estado de todos los consentimientos: Pendiente, Enviado, Firmado, Caducado.' },
        { callout: { type: 'warning', text: 'No inicies un tratamiento sin verificar los consentimientos obligatorios.' } },
      ],
    },
    tags: ['consentimiento', 'verificar', 'tratamiento', 'firma'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },

  // === LAB (47-50) ===
  {
    id: 'tut-047',
    slug: 'crear-orden-laboratorio',
    title: 'Crear una orden de laboratorio',
    description: 'Guia para solicitar un trabajo al laboratorio desde la ficha del paciente.',
    category: 'lab',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'interactive-tour',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde la ficha del paciente, ve a Laboratorio > Nueva orden.' },
        { heading: 'Paso 2', body: 'Selecciona el tipo de trabajo (corona, protesis, ferula...).' },
        { heading: 'Paso 3', body: 'Describe las especificaciones y selecciona el proveedor.' },
        { heading: 'Paso 4', body: 'Adjunta fotos o modelos digitales si es necesario.' },
      ],
    },
    tags: ['laboratorio', 'orden', 'crear', 'proveedor'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-048',
    slug: 'seguimiento-ordenes-laboratorio',
    title: 'Seguimiento de ordenes: estados y deadlines',
    description: 'Como monitorizar el estado de los trabajos de laboratorio.',
    category: 'lab',
    difficulty: 'beginner',
    roles: ['DENTIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Estados', body: 'Pendiente, Enviada, En proceso, Lista para entrega, Entregada, Rechazada.' },
        { heading: 'Deadlines', body: 'Cada orden tiene una fecha estimada de entrega. El sistema alerta cuando se acerca el deadline.' },
        { heading: 'Acciones', body: 'Puedes consultar el estado, contactar al proveedor o cancelar la orden.' },
      ],
    },
    tags: ['laboratorio', 'seguimiento', 'estados', 'deadline'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-049',
    slug: 'gestionar-proveedores-laboratorio',
    title: 'Gestionar proveedores de laboratorio',
    description: 'Como configurar y mantener la lista de laboratorios externos.',
    category: 'lab',
    difficulty: 'intermediate',
    roles: ['ADMIN', 'MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Laboratorio > Proveedores.' },
        { heading: 'Paso 2', body: 'Anade un nuevo proveedor con sus datos de contacto y especialidades.' },
        { heading: 'Paso 3', body: 'Configura tarifas por tipo de trabajo y plazos de entrega.' },
        { callout: { type: 'info', text: 'Puedes tener multiples proveedores y asignar el mas adecuado para cada tipo de trabajo.' } },
      ],
    },
    tags: ['laboratorio', 'proveedores', 'gestionar', 'tarifas'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-050',
    slug: 'registrar-recepcion-trabajo-laboratorio',
    title: 'Registrar la recepcion de un trabajo',
    description: 'Como dar por entregado un trabajo de laboratorio y vincularlo al paciente.',
    category: 'lab',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Cuando llega el trabajo, busca la orden en Laboratorio > Ordenes.' },
        { heading: 'Paso 2', body: 'Verifica que coincida con lo solicitado.' },
        { heading: 'Paso 3', body: 'Marca como "Entregada" y notifica al doctor.' },
        { callout: { type: 'tip', text: 'Puedes adjuntar una foto del trabajo recibido para registro.' } },
      ],
    },
    tags: ['laboratorio', 'recepcion', 'entrega', 'trabajo'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },

  // === INSURANCE (51-53) ===
  {
    id: 'tut-051',
    slug: 'asociar-poliza-paciente',
    title: 'Asociar una poliza de seguro a un paciente',
    description: 'Como registrar la cobertura de seguro en la ficha del paciente.',
    category: 'insurance',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En la ficha del paciente, ve a la pestana "Seguros".' },
        { heading: 'Paso 2', body: 'Haz clic en "Anadir poliza" y selecciona la aseguradora.' },
        { heading: 'Paso 3', body: 'Introduce el numero de poliza, titular y fecha de validez.' },
        { heading: 'Paso 4', body: 'Configura el tipo de cobertura y el porcentaje.' },
      ],
    },
    tags: ['seguro', 'poliza', 'paciente', 'cobertura'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-052',
    slug: 'verificar-cobertura-tratamiento',
    title: 'Verificar cobertura de un tratamiento',
    description: 'Como comprobar si un tratamiento esta cubierto por la poliza del paciente.',
    category: 'insurance',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Desde el presupuesto o tratamiento, haz clic en "Verificar cobertura".' },
        { heading: 'Paso 2', body: 'El sistema consulta la poliza activa y muestra el porcentaje cubierto.' },
        { heading: 'Paso 3', body: 'Si requiere autorizacion previa, el sistema genera el documento de solicitud.' },
        { callout: { type: 'info', text: 'Algunas aseguradoras requieren preautorizacion para tratamientos mayores.' } },
      ],
    },
    tags: ['seguro', 'cobertura', 'tratamiento', 'verificar'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-053',
    slug: 'generar-facturas-aseguradoras',
    title: 'Generar facturas a aseguradoras',
    description: 'Flujo para facturar mensualmente o por tratamiento a las companias de seguros.',
    category: 'insurance',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Seguros > Facturacion a aseguradoras.' },
        { heading: 'Paso 2', body: 'Selecciona la aseguradora y el periodo.' },
        { heading: 'Paso 3', body: 'El sistema agrupa todos los tratamientos cubiertos pendientes de facturar.' },
        { heading: 'Paso 4', body: 'Genera la factura y exportala en el formato requerido por la aseguradora.' },
      ],
    },
    tags: ['factura', 'aseguradora', 'seguro', 'facturacion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },

  // === REPORTS (54-59) ===
  {
    id: 'tut-054',
    slug: 'dashboard-ejecutivo-kpis',
    title: 'Dashboard ejecutivo: que KPIs vigilar',
    description: 'Guia para interpretar los indicadores clave del dashboard ejecutivo.',
    category: 'reports',
    difficulty: 'intermediate',
    roles: ['MANAGER', 'ADMIN'],
    format: 'video',
    duration: 5,
    content: {
      sections: [
        { heading: 'Ingresos y tendencia', body: 'Ingresos del mes, comparativa con el mes anterior y tendencia de los ultimos 6 meses.' },
        { heading: 'Ocupacion', body: 'Porcentaje de ocupacion de gabinetes y citas por profesional.' },
        { heading: 'Productividad', body: 'Ingresos por doctor, tratamientos completados y tiempo medio de consulta.' },
        { heading: 'No-shows y cancelaciones', body: 'Tasa de ausencias y cancelaciones. Valores superiores al 10% requieren atencion.' },
      ],
    },
    tags: ['kpi', 'dashboard', 'ejecutivo', 'metricas'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-055',
    slug: 'benchmark-interclinica',
    title: 'Interpretar el benchmark interclinica',
    description: 'Como comparar los KPIs de tu clinica con otras clinicas similares.',
    category: 'reports',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'article',
    duration: 4,
    content: {
      sections: [
        { heading: 'Que es el benchmark', body: 'Comparativa anonimizada de KPIs entre clinicas de caracteristicas similares (tamanio, zona, especialidades).' },
        { heading: 'KPIs comparados', body: 'Ingresos medios por paciente, tasa de aceptacion de presupuestos, ocupacion, no-shows.' },
        { heading: 'Como usarlo', body: 'Identifica areas donde tu clinica esta por debajo de la media y establece objetivos de mejora.' },
        { callout: { type: 'tip', text: 'Los datos del benchmark se actualizan trimestralmente.' } },
      ],
    },
    tags: ['benchmark', 'interclinica', 'comparativa', 'kpi'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-056',
    slug: 'productividad-por-profesional',
    title: 'Productividad por profesional: como leer la tabla',
    description: 'Interpretacion de la tabla de productividad con metricas por doctor.',
    category: 'reports',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Columnas principales', body: 'Pacientes atendidos, ingresos generados, tratamientos iniciados/completados, tiempo medio de consulta.' },
        { heading: 'Filtros', body: 'Filtra por periodo, comparativa vs mes anterior y vs media de la clinica.' },
        { heading: 'Drill-down', body: 'Haz clic en un doctor para ver su detalle: evolucion mensual, ranking y tratamientos mas frecuentes.' },
      ],
    },
    tags: ['productividad', 'profesional', 'doctor', 'tabla'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-057',
    slug: 'scorecards-por-doctor',
    title: 'Scorecards por doctor: metricas de rendimiento',
    description: 'Como interpretar las scorecards individuales de cada profesional.',
    category: 'reports',
    difficulty: 'advanced',
    roles: ['MANAGER'],
    format: 'article',
    duration: 5,
    content: {
      sections: [
        { heading: 'Metricas principales', body: 'Ingresos, pacientes, tasa de conversion de presupuestos, satisfaccion del paciente, puntualidad.' },
        { heading: 'Percentiles', body: 'Cada metrica muestra el percentil del doctor dentro de la clinica. P80+ es excelente, P20- requiere atencion.' },
        { heading: 'Tendencia', body: 'Sparkline de 6 meses para ver la evolucion de cada metrica.' },
        { heading: 'Anomalias', body: 'El sistema detecta cambios bruscos y las marca para revision.' },
      ],
    },
    tags: ['scorecard', 'doctor', 'rendimiento', 'percentil'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-058',
    slug: 'kpis-por-tipo-tratamiento',
    title: 'KPIs por tipo de tratamiento',
    description: 'Analisis de rentabilidad y demanda por tipo de tratamiento.',
    category: 'reports',
    difficulty: 'advanced',
    roles: ['MANAGER'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Rentabilidad', body: 'Ingresos medios por tratamiento, coste de materiales, margen bruto.' },
        { heading: 'Demanda', body: 'Numero de tratamientos realizados, tendencia de crecimiento, estacionalidad.' },
        { heading: 'Comparativa', body: 'Compara la rentabilidad entre tipos de tratamiento para optimizar la oferta.' },
      ],
    },
    tags: ['kpi', 'tratamiento', 'rentabilidad', 'demanda'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },
  {
    id: 'tut-059',
    slug: 'que-hacer-anomalia-detectada',
    title: 'Que hacer cuando se detecta una anomalia',
    description: 'Protocolo de actuacion ante alertas del sistema de deteccion de anomalias.',
    category: 'reports',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Tipos de anomalias', body: 'Caida de ingresos, aumento de no-shows, disminucion de aceptacion de presupuestos, cambios en productividad.' },
        { heading: 'Investigacion', body: 'Haz clic en la anomalia para ver el detalle: comparativa con periodos anteriores, profesionales afectados, posibles causas.' },
        { heading: 'Accion', body: 'Asigna tareas de seguimiento, programa una reunion con los profesionales implicados o ajusta procesos.' },
        { callout: { type: 'tip', text: 'No todas las anomalias son negativas. Un aumento repentino de ingresos tambien puede requerir atencion (capacidad, stock).' } },
      ],
    },
    tags: ['anomalia', 'alerta', 'protocolo', 'accion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 6,
  },

  // === MARKETING (60-62) ===
  {
    id: 'tut-060',
    slug: 'crear-campana-comunicacion',
    title: 'Crear una campana de comunicacion',
    description: 'Como disenar y lanzar una campana de email o SMS para pacientes.',
    category: 'marketing',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Marketing > Campanas y haz clic en "Nueva campana".' },
        { heading: 'Paso 2', body: 'Elige el objetivo: recordatorio de revision, promocion, felicitacion, encuesta.' },
        { heading: 'Paso 3', body: 'Selecciona el segmento de pacientes destinatarios.' },
        { heading: 'Paso 4', body: 'Disena el mensaje, programa el envio y lanza la campana.' },
      ],
    },
    tags: ['campana', 'marketing', 'comunicacion', 'email'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-061',
    slug: 'segmentar-pacientes-envios',
    title: 'Segmentar pacientes para envios',
    description: 'Como crear grupos de pacientes basados en criterios especificos.',
    category: 'marketing',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Marketing > Segmentos y crea un nuevo segmento.' },
        { heading: 'Paso 2', body: 'Define criterios: edad, ultima visita, tratamiento recibido, profesional asignado.' },
        { heading: 'Paso 3', body: 'Previsualiza el segmento para ver cuantos pacientes lo cumplen.' },
        { callout: { type: 'tip', text: 'Los segmentos se actualizan automaticamente: si un paciente cumple los criterios, se incluye en futuros envios.' } },
      ],
    },
    tags: ['segmentar', 'pacientes', 'marketing', 'criterios'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-062',
    slug: 'programar-recordatorios-revisiones',
    title: 'Programar recordatorios automaticos de revisiones',
    description: 'Como configurar envios automaticos para revisiones periodicas.',
    category: 'marketing',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST', 'MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Recordatorios > Automatizaciones.' },
        { heading: 'Paso 2', body: 'Crea una regla: "Enviar recordatorio de revision 6 meses despues de la ultima limpieza".' },
        { heading: 'Paso 3', body: 'Configura el canal, el mensaje y la hora de envio.' },
        { callout: { type: 'info', text: 'Los recordatorios automaticos reducen la tasa de pacientes que no vuelven.' } },
      ],
    },
    tags: ['recordatorios', 'automaticos', 'revision', 'marketing'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },

  // === RECEPTION (63-65) ===
  {
    id: 'tut-063',
    slug: 'check-in-pacientes',
    title: 'Check-in de pacientes: flujo de llegada',
    description: 'Como registrar la llegada de un paciente y gestionar el flujo de sala de espera.',
    category: 'reception',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'interactive-tour',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En Recepcion, busca al paciente por nombre o escanea su codigo QR.' },
        { heading: 'Paso 2', body: 'Haz clic en "Check-in". El paciente aparece en la pantalla de sala de espera.' },
        { heading: 'Paso 3', body: 'Cuando el doctor este listo, marca "Llamar" para notificar al paciente.' },
      ],
    },
    tags: ['check-in', 'recepcion', 'llegada', 'sala de espera'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-064',
    slug: 'pantalla-sala-espera',
    title: 'Pantalla de sala de espera',
    description: 'Como configurar y usar la pantalla de sala de espera para los pacientes.',
    category: 'reception',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Recepcion > Pantalla de espera.' },
        { heading: 'Paso 2', body: 'Configura el modo: lista simple, con tiempos estimados, o anonimizado (solo numero de turno).' },
        { heading: 'Paso 3', body: 'Proyecta la pantalla en una TV de la sala de espera via URL publica.' },
        { callout: { type: 'tip', text: 'La URL de la pantalla es publica pero no muestra datos personales sensibles.' } },
      ],
    },
    tags: ['sala de espera', 'pantalla', 'recepcion', 'tv'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-065',
    slug: 'gestionar-cobros-mostrador',
    title: 'Gestionar cobros en mostrador',
    description: 'Flujo de cobro para pacientes que pagan tras la consulta.',
    category: 'reception',
    difficulty: 'beginner',
    roles: ['RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Cuando el paciente finaliza, el sistema muestra un aviso de cobro pendiente.' },
        { heading: 'Paso 2', body: 'Accede a la factura desde el aviso o desde Facturacion.' },
        { heading: 'Paso 3', body: 'Registra el pago con el metodo elegido y entrega el justificante.' },
        { callout: { type: 'tip', text: 'Puedes configurar recordatorios de pago para pacientes que no pagan en el momento.' } },
      ],
    },
    tags: ['cobros', 'mostrador', 'recepcion', 'pago'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },

  // === INVENTORY (66-67) ===
  {
    id: 'tut-066',
    slug: 'control-stock-alertas',
    title: 'Control de stock: alertas y reposicion',
    description: 'Como monitorizar los niveles de inventario y gestionar las alertas de stock bajo.',
    category: 'inventory',
    difficulty: 'intermediate',
    roles: ['MANAGER', 'ADMIN'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Stock > Alertas.' },
        { heading: 'Paso 2', body: 'Revisa los productos con stock por debajo del minimo configurado.' },
        { heading: 'Paso 3', body: 'Genera ordenes de compra directamente desde las alertas.' },
        { callout: { type: 'info', text: 'Puedes configurar el stock minimo y maximo por producto.' } },
      ],
    },
    tags: ['stock', 'inventario', 'alertas', 'reposicion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-067',
    slug: 'orden-compra-proveedor',
    title: 'Crear una orden de compra a proveedor',
    description: 'Flujo para generar un pedido de reposicion de material.',
    category: 'inventory',
    difficulty: 'intermediate',
    roles: ['MANAGER'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Stock > Ordenes de compra > Nueva orden.' },
        { heading: 'Paso 2', body: 'Selecciona el proveedor y anade los productos con sus cantidades.' },
        { heading: 'Paso 3', body: 'Revisa el total, confirma y envia la orden por email.' },
        { heading: 'Paso 4', body: 'Al recibir el pedido, marca la orden como recibida y actualiza el stock.' },
      ],
    },
    tags: ['orden', 'compra', 'proveedor', 'stock'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },

  // === ADMIN (68-72) ===
  {
    id: 'tut-068',
    slug: 'crear-usuario-asignar-rol',
    title: 'Crear un nuevo usuario y asignar rol',
    description: 'Guia para dar de alta a un nuevo miembro del equipo en el sistema.',
    category: 'admin',
    difficulty: 'beginner',
    roles: ['ADMIN'],
    format: 'interactive-tour',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Administracion > Usuarios y haz clic en "Nuevo usuario".' },
        { heading: 'Paso 2', body: 'Completa los datos personales y el email (servira para el login).' },
        { heading: 'Paso 3', body: 'Selecciona el rol: Admin, Manager, Dentista, Higienista o Recepcionista.' },
        { heading: 'Paso 4', body: 'El usuario recibira un email para establecer su contrasena.' },
      ],
    },
    tags: ['usuario', 'crear', 'rol', 'equipo'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-069',
    slug: 'configurar-permisos-rol',
    title: 'Configurar permisos por rol',
    description: 'Como personalizar que puede ver y hacer cada rol de forma granular.',
    category: 'admin',
    difficulty: 'intermediate',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Administracion > Permisos.' },
        { heading: 'Paso 2', body: 'Selecciona el rol que quieres configurar.' },
        { heading: 'Paso 3', body: 'Marca o desmarca los permisos especificos: lectura, escritura, eliminacion.' },
        { heading: 'Paso 4', body: 'Guarda los cambios. Se aplican inmediatamente.' },
        { callout: { type: 'warning', text: 'El rol Admin siempre tiene todos los permisos y no se puede restringir.' } },
      ],
    },
    tags: ['permisos', 'rol', 'configurar', 'acceso'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-070',
    slug: 'configurar-datos-clinica',
    title: 'Configurar datos de la clinica',
    description: 'Como completar la informacion basica de la clinica en el sistema.',
    category: 'admin',
    difficulty: 'beginner',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Administracion > Clinicas y selecciona tu clinica.' },
        { heading: 'Paso 2', body: 'Completa: nombre, direccion, telefono, email, CIF/NIF, horario.' },
        { heading: 'Paso 3', body: 'Sube el logo para que aparezca en presupuestos, facturas y el portal del paciente.' },
      ],
    },
    tags: ['clinica', 'datos', 'configurar', 'logo'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-071',
    slug: 'consultar-log-auditoria',
    title: 'Consultar el log de auditoria',
    description: 'Como revisar el historial de acciones realizadas en el sistema.',
    category: 'admin',
    difficulty: 'intermediate',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Administracion > Auditoria.' },
        { heading: 'Paso 2', body: 'Usa los filtros: usuario, modulo, tipo de accion, fecha.' },
        { heading: 'Paso 3', body: 'Haz clic en cualquier entrada para ver los detalles de la accion.' },
        { callout: { type: 'info', text: 'El log de auditoria es inmutable y sirve como evidencia legal de las acciones en el sistema.' } },
      ],
    },
    tags: ['auditoria', 'log', 'acciones', 'historial'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-072',
    slug: 'gestionar-multiples-sedes',
    title: 'Gestionar multiples sedes',
    description: 'Como configurar y operar con varias clinicas desde una sola cuenta.',
    category: 'admin',
    difficulty: 'advanced',
    roles: ['ADMIN'],
    format: 'step-by-step',
    duration: 4,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'Ve a Administracion > Clinicas > Nueva sede.' },
        { heading: 'Paso 2', body: 'Completa los datos de la nueva sede y asigna usuarios.' },
        { heading: 'Paso 3', body: 'Configura si los usuarios pueden ver datos de una sola sede o de todas.' },
        { heading: 'Paso 4', body: 'Los informes pueden mostrar datos por sede o consolidados.' },
        { callout: { type: 'tip', text: 'El benchmark interclinica compara el rendimiento entre tus propias sedes.' } },
      ],
    },
    tags: ['sedes', 'multiples', 'clinicas', 'gestionar'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },

  // === TIPS (73-78) ===
  {
    id: 'tut-073',
    slug: '10-atajos-teclado',
    title: '10 atajos de teclado que ahorran tiempo',
    description: 'Referencia rapida de los atajos mas utiles de SmartDental.',
    category: 'tips',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'cheatsheet',
    duration: 2,
    content: {
      sections: [
        { heading: 'Navegacion', body: 'Ctrl + K: Paleta de comandos\nN: Nueva cita\nEsc: Cerrar modal/dropdown' },
        { heading: 'Acciones', body: 'Ctrl + S: Guardar\nCtrl + P: Imprimir\nCtrl + F: Buscar en pagina' },
        { heading: 'Calendario', body: 'Flechas: Navegar entre dias\nEnter: Abrir cita seleccionada\nDel: Eliminar cita seleccionada' },
      ],
    },
    tags: ['atajos', 'teclado', 'rapido', 'trucos'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 1,
  },
  {
    id: 'tut-074',
    slug: 'command-palette-experto',
    title: 'Usar el Command Palette como un experto',
    description: 'Tecnicas avanzadas de la paleta de comandos para navegar a velocidad.',
    category: 'tips',
    difficulty: 'intermediate',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 2,
    content: {
      sections: [
        { heading: 'Buscar pacientes', body: 'Escribe el nombre, DNI o telefono. Enter para abrir la ficha.' },
        { heading: 'Navegar', body: 'Escribe el nombre de la pagina: "pacientes", "calendario", "informes".' },
        { heading: 'Acciones rapidas', body: '"nueva cita", "nuevo paciente", "exportar" ejecutan la accion directamente.' },
        { callout: { type: 'tip', text: 'El Command Palette aprende de tus busquedas frecuentes y las sugiere primero.' } },
      ],
    },
    tags: ['command palette', 'atajos', 'busqueda', 'navegacion'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 2,
  },
  {
    id: 'tut-075',
    slug: 'personalizar-dashboard',
    title: 'Personalizar tu dashboard',
    description: 'Como ajustar los widgets y el layout del panel principal.',
    category: 'tips',
    difficulty: 'intermediate',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'step-by-step',
    duration: 3,
    content: {
      sections: [
        { heading: 'Paso 1', body: 'En el dashboard, haz clic en "Personalizar" en la esquina superior.' },
        { heading: 'Paso 2', body: 'Arrastra los widgets para reorganizarlos.' },
        { heading: 'Paso 3', body: 'Anade o elimina widgets del catalogo disponible.' },
        { heading: 'Paso 4', body: 'Guarda la configuracion. Cada usuario tiene su propio layout.' },
      ],
    },
    tags: ['dashboard', 'personalizar', 'widgets', 'layout'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 3,
  },
  {
    id: 'tut-076',
    slug: 'tips-reducir-no-shows',
    title: 'Tips para reducir los no-shows',
    description: 'Estrategias probadas para minimizar las ausencias a las citas.',
    category: 'tips',
    difficulty: 'intermediate',
    roles: ['RECEPTIONIST', 'MANAGER'],
    format: 'article',
    duration: 4,
    content: {
      sections: [
        { heading: 'Recordatorios multicanal', body: 'Envia recordatorios por email (48h antes) y SMS (24h antes).' },
        { heading: 'Confirmacion activa', body: 'Pide al paciente que confirme la cita respondiendo al mensaje.' },
        { heading: 'Lista de espera', body: 'Manten una lista de espera activa para ocupar huecos de ultima hora.' },
        { heading: 'Identifica patrones', body: 'El sistema marca pacientes con alto riesgo de no-show para que reciban seguimiento extra.' },
      ],
    },
    tags: ['no-shows', 'ausencias', 'reducir', 'estrategias'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 4,
  },
  {
    id: 'tut-077',
    slug: 'buenas-practicas-seguridad-rgpd',
    title: 'Buenas practicas de seguridad y RGPD',
    description: 'Guia de seguridad para proteger los datos de los pacientes.',
    category: 'tips',
    difficulty: 'beginner',
    roles: ['ADMIN', 'MANAGER', 'DENTIST', 'HYGIENIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 5,
    content: {
      sections: [
        { heading: 'Contrasenas', body: 'Usa contrasenas fuertes, unicas y activa la autenticacion de dos factores si esta disponible.' },
        { heading: 'Acceso', body: 'No compartas tu cuenta. Cierra sesion al terminar, especialmente en equipos compartidos.' },
        { heading: 'Datos', body: 'No exportes datos de pacientes sin autorizacion. No envies informacion sensible por canales no seguros.' },
        { heading: 'Derechos del paciente', body: 'Responde a las solicitudes de acceso, rectificacion y olvido en el plazo legal de 30 dias.' },
        { callout: { type: 'warning', text: 'Las infracciones al RGPD pueden conllevar sanciones de hasta 20 millones de euros.' } },
      ],
    },
    tags: ['seguridad', 'rgpd', 'proteccion', 'datos'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 5,
  },
  {
    id: 'tut-078',
    slug: 'interpretar-riesgo-no-show',
    title: 'Como interpretar el riesgo de no-show de un paciente',
    description: 'Entiende la prediccion de ausencia y como actuar ante riesgo alto.',
    category: 'tips',
    difficulty: 'advanced',
    roles: ['DENTIST', 'RECEPTIONIST'],
    format: 'article',
    duration: 3,
    content: {
      sections: [
        { heading: 'Que es el riesgo de no-show', body: 'Una puntuacion de 0 a 100 basada en el historial de ausencias, cancelaciones, antelacion de la cita y otros factores.' },
        { heading: 'Niveles de riesgo', body: 'Bajo (0-30): normal. Medio (31-60): enviar recordatorio extra. Alto (61-100): llamada telefonica de confirmacion.' },
        { heading: 'Como usarlo', body: 'Revisa el riesgo al agendar la cita y decide el protocolo de seguimiento adecuado.' },
      ],
    },
    tags: ['no-show', 'riesgo', 'prediccion', 'interpretar'],
    publishedAt: '2025-01-01',
    updatedAt: '2025-01-01',
    isNew: false,
    sortOrder: 6,
  },
]

export const categoryLabels: Record<string, string> = {
  'getting-started': 'Primeros pasos',
  'agenda': 'Agenda y citas',
  'patients': 'Pacientes',
  'clinical': 'Historia clinica',
  'treatments': 'Tratamientos',
  'budgets': 'Presupuestos',
  'billing': 'Facturacion',
  'consents': 'Consentimientos',
  'lab': 'Laboratorio',
  'insurance': 'Seguros',
  'reports': 'Informes y KPIs',
  'marketing': 'Marketing y comunicaciones',
  'reception': 'Recepcion',
  'inventory': 'Inventario y stock',
  'reputation': 'Reputacion',
  'admin': 'Administracion',
  'integrations': 'Integraciones',
  'tips': 'Trucos y atajos',
}

export const categoryIcons: Record<string, string> = {
  'getting-started': 'Sparkles',
  'agenda': 'Calendar',
  'patients': 'Users',
  'clinical': 'Stethoscope',
  'treatments': 'Scissors',
  'budgets': 'BarChart3',
  'billing': 'CreditCard',
  'consents': 'FileSignature',
  'lab': 'FlaskConical',
  'insurance': 'Shield',
  'reports': 'BarChart3',
  'marketing': 'Megaphone',
  'reception': 'Monitor',
  'inventory': 'Package',
  'reputation': 'Star',
  'admin': 'ShieldCheck',
  'integrations': 'Link',
  'tips': 'Zap',
}

export const difficultyLabels: Record<string, string> = {
  beginner: 'Basico',
  intermediate: 'Intermedio',
  advanced: 'Avanzado',
}

export const difficultyColors: Record<string, string> = {
  beginner: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  intermediate: 'bg-amber-50 text-amber-700 border-amber-200',
  advanced: 'bg-rose-50 text-rose-700 border-rose-200',
}

export const formatIcons: Record<string, string> = {
  article: 'FileText',
  video: 'PlayCircle',
  'interactive-tour': 'Map',
  'step-by-step': 'ListOrdered',
  faq: 'HelpCircle',
  cheatsheet: 'ScrollText',
}

export const formatLabels: Record<string, string> = {
  article: 'Articulo',
  video: 'Video',
  'interactive-tour': 'Tour interactivo',
  'step-by-step': 'Paso a paso',
  faq: 'FAQ',
  cheatsheet: 'Referencia',
}

export function getTutorialBySlug(slug: string): Tutorial | undefined {
  return tutorials.find((t) => t.slug === slug)
}

export function getTutorialsByCategory(category: string): Tutorial[] {
  return tutorials.filter((t) => t.category === category).sort((a, b) => a.sortOrder - b.sortOrder)
}

export function searchTutorials(query: string): Tutorial[] {
  const q = query.toLowerCase().trim()
  if (!q) return tutorials
  return tutorials.filter(
    (t) =>
      t.title.toLowerCase().includes(q) ||
      t.description.toLowerCase().includes(q) ||
      t.tags.some((tag) => tag.toLowerCase().includes(q))
  )
}

export function getTutorialsForRole(role: string): Tutorial[] {
  return tutorials.filter((t) => t.roles.includes(role as RoleKey) || t.roles.length === 5)
}
